import { create } from 'zustand';

/**
 * Store de visibilidade de departamentos no WhatsApp.
 *
 * Controla quais departamentos o usuario pode ver as mensagens.
 * Mensagens de departamentos nao autorizados sao borradas/ocultas
 * (a conversa continua visivel, so o conteudo e escondido).
 *
 * Configurado por grupo de usuario na tela de permissoes.
 */

export interface DeptVisibilityConfig {
  /** Se true, usuario so ve mensagens do proprio departamento + extras */
  restrictByDept: boolean;
  /** Departamento principal do usuario (vem do grupo) */
  userDept: string;
  /** Departamentos extras que o usuario pode ver alem do seu */
  allowedExtraDepts: string[];
  /** Se true, oculta (borra) mensagens ao inves de esconder completamente */
  blurMessages: boolean;
}

interface DeptVisibilityState extends DeptVisibilityConfig {
  setConfig: (config: Partial<DeptVisibilityConfig>) => void;
  /** Verifica se o usuario pode ver mensagens de um departamento */
  canSeeDept: (dept: string) => boolean;
}

export const useDeptVisibilityStore = create<DeptVisibilityState>()((set, get) => ({
  restrictByDept: false,
  userDept: '',
  allowedExtraDepts: [],
  blurMessages: true,

  setConfig: config => set(s => ({ ...s, ...config })),

  canSeeDept: dept => {
    const state = get();
    // Se nao tem restricao, ve tudo
    if (!state.restrictByDept) return true;
    // Se nao tem departamento configurado, ve tudo
    if (!state.userDept) return true;
    // Se nao informou o departamento da conversa, libera (sem info pra restringir)
    if (!dept) return true;

    const normalize = (s: string) => s.toLowerCase().trim();
    const userDeptNorm = normalize(state.userDept);
    const deptNorm = normalize(dept);

    // Extrai palavras-chave de cada departamento para comparacao mais robusta
    // Ex: "Fiscal Artistas" -> ["fiscal", "artistas"]
    const userWords = userDeptNorm.split(/[\s.,-]+/).filter(Boolean);
    const deptWords = deptNorm.split(/[\s.,-]+/).filter(Boolean);

    // Match exato
    if (deptNorm === userDeptNorm) return true;

    // Match parcial por conteudo (ex: "Fiscal Artistas" inclui "Fiscal")
    if (userDeptNorm.includes(deptNorm) || deptNorm.includes(userDeptNorm)) return true;

    // Match por primeira palavra-chave (a raiz do departamento)
    // Ex: "Fiscal Artistas" e "Fiscal" compartilham a raiz "fiscal"
    if (userWords.length > 0 && deptWords.length > 0 && userWords[0] === deptWords[0]) return true;

    // Departamentos extras permitidos
    if (
      state.allowedExtraDepts.some(d => {
        const n = normalize(d);
        const extraWords = n.split(/[\s.,-]+/).filter(Boolean);
        return (
          n === deptNorm ||
          n.includes(deptNorm) ||
          deptNorm.includes(n) ||
          (extraWords.length > 0 && deptWords.length > 0 && extraWords[0] === deptWords[0])
        );
      })
    )
      return true;

    return false;
  },
}));

/** Lista de departamentos disponiveis no sistema */
export const AVAILABLE_DEPARTMENTS = [
  'Fiscal',
  'Contabil',
  'Depto. Pessoal',
  'Societario',
  'Consultoria',
  'Atendimento',
];
